import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ButtonGroup,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Menu,
  IconButton,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  LocationOn,
  VideoCall,
  Phone,
  CheckCircle,
  Cancel,
  Schedule,
  Send,
  Launch,
  Replay,
  Person,
  HourglassEmpty,
  KeyboardArrowDown,
  ArrowDropDown,
  ChevronRight,
  Message,
  Close,
  Visibility,
} from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { Field } from '@campfire/field';
import { RichEditorFormik, CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { convertFromRaw, EditorState } from 'draft-js';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { DateTime } from 'luxon';
import { TaskFormFieldList } from '../../../../../../../common/form/task-form/TaskFormFieldList';
import { deserializeTaskItems } from '../../../../../../admin/admin-console/admin-console-content-pages/admin-console-volunteer-profile/form-builder/utils';
import { initTaskFormFormikValues } from '../../../../../../../common/form/task-form/model/task-form-init';
import { AutoLocationField } from '../../../../../../../common/auto-location-field/AutoLocationField';
import { AlertCard, AlertCardBody, getAlertCardColors } from '../../../../../../../common/cards/alert-card/AlertCard';
import { useRegionCodes } from '../../../../../../../common/auto-location-field/use-region-codes/use-region-codes';
import { useSnackbar } from '../../../../../../../global/config/useSnackbar';
import { useApiUrl } from '../../../../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../../../../global/network/useCampfireFetch';
import { useCampfireQuery } from '../../../../../../../global/network/useCampfireQuery';
import { VolunteerProfile } from '../../../../../../../common/AvatarName';
import { ConfirmationDialog } from '../../../../../../../common/dialogs/ConfirmationDialog';
import {
  MultiSelectVolunteersDialog,
  MultiSelectVolunteersDialogVolunteerType,
} from '../../../../../activities/activity-timeline/past/report/form/MultiSelectVolunteersDialog';
import { GET_MANAGERS_FOR_EVENT } from './incoming-volunteer-event-view.gql';
import { useUser } from '../../../../../../../global/auth/useUser';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    eventItem: {
      padding: '20px',
      marginBottom: '16px',
      border: '1px solid #e0e0e0',
      borderRadius: '16px',
      backgroundColor: '#ffffff',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        transform: 'translateY(-1px)',
      },
    },
    eventInfo: {
      display: 'flex',
      alignItems: 'flex-start',
      marginBottom: '12px',
      '&:last-child': {
        marginBottom: 0,
      },
    },
    eventInfoIcon: {
      fontSize: '18px',
      color: '#757575',
      marginRight: '12px',
      marginTop: '2px',
      flexShrink: 0,
    },
    statusChip: {
      marginLeft: '8px',
      fontSize: '13px',
      height: '28px',
      fontWeight: 600,
    },
    cancelledEvent: {
      marginBottom: theme.spacing(2),
    },
    // Volaby button styles
    button: {
      border: '1px solid',
      boxSizing: 'border-box',
      borderRadius: '4px',
      textTransform: 'none',
      padding: '7px 12px',
      minWidth: '45px',
      fontWeight: 600,
      fontSize: '13px',
      lineHeight: '19px',
    },
    toggleButton: {
      color: '#666',
      borderColor: '#dcdcdc',
      backgroundColor: '#fff',
      '&:hover': {
        backgroundColor: '#f5f5f5',
      },
    },
    toggleButtonSelected: {
      color: '#FCFCFC !important',
      backgroundColor: `${theme.palette.primary.main} !important`,
      borderColor: `${theme.palette.primary.main} !important`,
      '&:hover': {
        backgroundColor: `${theme.palette.primary.dark} !important`,
      },
    },
    // Meeting button styles
    meetingButtonContainer: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      width: '100%',
      marginTop: '4px',
      marginBottom: '4px',
    },
    meetingButton: {
      fontSize: '13px',
      fontWeight: 600,
      textTransform: 'none',
      borderRadius: '12px',
      padding: '10px 18px',
      minWidth: '160px',
      boxShadow: '0 3px 12px rgba(0,0,0,0.2)',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
        transform: 'translateY(-2px)',
      },
    },
  })
);

interface EventData {
  completedTaskEventId: string;
  startDateTime?: string;
  endDateTime?: string;
  timezone?: string;
  status: 'scheduled' | 'completed' | 'cancelled' | 'no_show';
  placesAddress?: {
    placesId: string;
    description: string;
    formatted: string;
    latLong?: {
      lat: number;
      long: number;
    };
    name: string;
    address: string;
  };
  remoteLocation?: string;
  managers?: Array<{
    managerId: string;
    profile: {
      profileId: string;
      firstName: string;
      lastName: string;
      preferredName: string;
      avatarUrl: string | null;
    };
  }>;
  participantFeedback?: string;
  volunteerMessage?: string; // Message included for the volunteer
  rescheduleCount: number;
  originalScheduledDate?: string;
  dateCreated: string;
}

interface IncomingVolunteerEventViewProps {
  events: EventData[];
  taskId: string;
  profileId: string;
  programId?: string;
  onRefresh?: () => void;
  isReadOnly?: boolean;
  taskItems?: any[];
  taskItemValues?: { [key: string]: any };
}

const validationSchema = Yup.object({
  startDateTime: Yup.string().required('Start date and time is required'),
  endDateTime: Yup.string()
    .nullable()
    .test('is-after-start', '', function validateEndTime(value) {
      const { startDateTime } = this.parent;

      // If no end time provided, validation passes (it's optional)
      if (!value) return true;

      // If no start time, can't validate end time yet
      if (!startDateTime) return true;

      // Parse both dates and compare
      const startDate = DateTime.fromISO(startDateTime);
      const endDate = DateTime.fromISO(value);

      // Check if dates are valid
      if (!startDate.isValid || !endDate.isValid) return true;

      // End time must be after start time
      return endDate > startDate;
    }),
  timezone: Yup.string().required('Timezone is required'),
  locationType: Yup.string()
    .oneOf(['local', 'remote'])
    .required('Location type is required'),
  location: Yup.mixed().when('locationType', {
    is: 'local',
    then: Yup.mixed().required('Please select a specific location'),
    otherwise: Yup.mixed().nullable(),
  }),
  remoteLocation: Yup.string().when('locationType', {
    is: 'remote',
    then: Yup.string().required('Please enter remote location details'),
    otherwise: Yup.string().nullable(),
  }),
});

// Timezone options by region code
const timezonesByRegion = {
  au: [
    { value: 'Australia/Sydney', label: 'Sydney/Melbourne (AEST/AEDT)' },
    { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
    { value: 'Australia/Perth', label: 'Perth (AWST)' },
    { value: 'Australia/Adelaide', label: 'Adelaide (ACST/ACDT)' },
    { value: 'Australia/Darwin', label: 'Darwin (ACST)' },
    { value: 'Australia/Hobart', label: 'Hobart (AEST/AEDT)' },
  ],
  nz: [
    { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
    { value: 'Pacific/Chatham', label: 'Chatham Islands (CHAST/CHADT)' },
  ],
  us: [
    { value: 'America/New_York', label: 'Eastern Time (EST/EDT)' },
    { value: 'America/Chicago', label: 'Central Time (CST/CDT)' },
    { value: 'America/Denver', label: 'Mountain Time (MST/MDT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PST/PDT)' },
    { value: 'America/Phoenix', label: 'Arizona (MST)' },
    { value: 'America/Anchorage', label: 'Alaska (AKST/AKDT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
  ],
  uk: [{ value: 'Europe/London', label: 'London (GMT/BST)' }],
  gb: [{ value: 'Europe/London', label: 'London (GMT/BST)' }],
  is: [{ value: 'Asia/Jerusalem', label: 'Jerusalem (IST)' }],
  // Dual zones
  'au,us': [
    { value: 'Australia/Sydney', label: 'Sydney/Melbourne (AEST/AEDT)' },
    { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
    { value: 'Australia/Perth', label: 'Perth (AWST)' },
    { value: 'Australia/Adelaide', label: 'Adelaide (ACST/ACDT)' },
    { value: 'Australia/Darwin', label: 'Darwin (ACST)' },
    { value: 'Australia/Hobart', label: 'Hobart (AEST/AEDT)' },
    { value: 'America/New_York', label: 'Eastern Time (EST/EDT)' },
    { value: 'America/Chicago', label: 'Central Time (CST/CDT)' },
    { value: 'America/Denver', label: 'Mountain Time (MST/MDT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PST/PDT)' },
    { value: 'America/Phoenix', label: 'Arizona (MST)' },
    { value: 'America/Anchorage', label: 'Alaska (AKST/AKDT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
  ],
  'nz,au': [
    { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
    { value: 'Pacific/Chatham', label: 'Chatham Islands (CHAST/CHADT)' },
    { value: 'Australia/Sydney', label: 'Sydney/Melbourne (AEST/AEDT)' },
    { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
    { value: 'Australia/Perth', label: 'Perth (AWST)' },
    { value: 'Australia/Adelaide', label: 'Adelaide (ACST/ACDT)' },
    { value: 'Australia/Darwin', label: 'Darwin (ACST)' },
    { value: 'Australia/Hobart', label: 'Hobart (AEST/AEDT)' },
  ],
};

const getTimezoneOptionsForRegion = (regionCode: string): Array<{ value: string; label: string }> => {
  return timezonesByRegion[regionCode as keyof typeof timezonesByRegion] || timezonesByRegion.au; // fallback to AU
};

const getDefaultTimezone = (timezoneOptions: Array<{ value: string; label: string }>) => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // First, try to find an exact match
  const exactMatch = timezoneOptions.find((option) => option.value === userTimezone);
  if (exactMatch) {
    return exactMatch.value;
  }

  // If no exact match, try to find a similar timezone in the same region
  // For example, if user is in Australia/Melbourne, default to Australia/Sydney
  const userRegion = userTimezone.split('/')[0];
  const sameRegionMatch = timezoneOptions.find((option) => option.value.startsWith(userRegion));
  if (sameRegionMatch) {
    return sameRegionMatch.value;
  }

  // Default to the first option available
  return timezoneOptions[0]?.value || 'Australia/Sydney';
};

const getDefaultTimes = () => {
  const now = new Date();
  const currentHour = now.getHours();
  const currentDay = now.getDay(); // 0 = Sunday, 6 = Saturday

  let defaultStartDate = new Date(now);

  // If it's after 5 PM or weekend, schedule for next business day at 9 AM
  if (currentHour >= 17 || currentDay === 0 || currentDay === 6) {
    // Find next business day (Monday-Friday)
    const nextBusinessDay = new Date(now);
    nextBusinessDay.setDate(now.getDate() + 1);

    while (nextBusinessDay.getDay() === 0 || nextBusinessDay.getDay() === 6) {
      nextBusinessDay.setDate(nextBusinessDay.getDate() + 1);
    }

    nextBusinessDay.setHours(9, 0, 0, 0);
    defaultStartDate = nextBusinessDay;
  } else {
    // If during business hours, set to next hour (rounded up) but at least 9 AM
    const nextHour = Math.max(currentHour + 1, 9);
    defaultStartDate.setHours(nextHour, 0, 0, 0);
  }

  // End time is start time + 1 hour
  const defaultEndDate = new Date(defaultStartDate);
  defaultEndDate.setHours(defaultStartDate.getHours() + 1);

  const formatDateTime = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  return {
    startDateTime: formatDateTime(defaultStartDate),
    endDateTime: formatDateTime(defaultEndDate),
  };
};

// Helper function to get current datetime string for min attribute
const getCurrentDateTimeString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const IncomingVolunteerEventView: React.FC<IncomingVolunteerEventViewProps> = ({
  events,
  taskId,
  profileId,
  programId,
  onRefresh,
  isReadOnly = false,
  taskItems = [],
  taskItemValues = {},
}) => {
  const classes = useStyles();
  const { setSnackbar } = useSnackbar();
  const apiUrl = useApiUrl();
  const { primaryRegion } = useRegionCodes();
  const timezoneOptions = React.useMemo(() => getTimezoneOptionsForRegion(primaryRegion || 'au'), [primaryRegion]);
  const { user } = useUser();

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventData | null>(null);
  const [showManagersDialog, setShowManagersDialog] = useState(false);
  const [selectedManagers, setSelectedManagers] = useState<MultiSelectVolunteersDialogVolunteerType[]>([]);

  // Debug useEffect to log selectedEvent changes
  React.useEffect(() => {}, [selectedEvent, selectedManagers]);

  const campfireMutation = useCampfireFetch({ defer: true });

  // Fetch managers for selection
  const managersQuery = useCampfireQuery(GET_MANAGERS_FOR_EVENT, {
    options: {
      variables: { programId },
      skip: !programId || !showManagersDialog,
    },
  });

  // Transform managers to volunteer format for the dialog
  const managersAsVolunteers = React.useMemo(() => {
    if (!managersQuery.data) return [];

    const data = managersQuery.data as any; // Type assertion to avoid unknown type issues
    const managers: MultiSelectVolunteersDialogVolunteerType[] = [];

    // Add program managers
    const programManagers = data.vm?.program?.programManagers || [];
    programManagers.forEach((pm: any) => {
      if (pm.manager) {
        managers.push({
          volunteerId: pm.manager.managerId, // Using managerId as volunteerId
          profile: {
            __typename: 'VOLUNTEER_ProfileType' as const,
            profileId: pm.manager.profile.profileId,
            userId: pm.manager.profile.userId,
            preferredName: pm.manager.profile.preferredName,
            lastName: pm.manager.profile.lastName,
            avatarUrl: pm.manager.profile.avatarUrl,
          },
        });
      }
    });

    // Add admins (deduplicating by managerId)
    const managerIds = new Set(managers.map((m) => m.volunteerId));
    const admins = data.vm?.admins || [];
    admins.forEach((admin: any) => {
      if (!managerIds.has(admin.managerId)) {
        managers.push({
          volunteerId: admin.managerId, // Using managerId as volunteerId
          profile: {
            __typename: 'VOLUNTEER_ProfileType' as const,
            profileId: admin.profile.profileId,
            userId: admin.profile.userId,
            preferredName: `${admin.profile.preferredName} (Admin)`,
            lastName: admin.profile.lastName,
            avatarUrl: admin.profile.avatarUrl,
          },
        });
      }
    });

    return managers;
  }, [managersQuery.data]);

  const handleScheduleEvent = (values: any) => {
    const payload: any = {
      taskId,
      profileId,
      startDateTime: values.startDateTime,
      timezone: values.timezone,
      locationType: values.locationType,
      assignedManagerIds: selectedManagers.map((m) => m.volunteerId),
      // Include eventId for rescheduling existing events
      ...(selectedEvent && { eventId: selectedEvent.completedTaskEventId }),
    };

    // Only include endDateTime if provided
    if (values.endDateTime) {
      payload.endDateTime = values.endDateTime;
    }

    // Only include location if it's a local event and location is selected
    if (values.locationType === 'local' && values.location) {
      payload.location = {
        placesAddress: {
          description: values.location.description,
          formatted: values.location.formatted,
          placesId: values.location.placesId,
          latitude: values.location.latitude,
          longitude: values.location.longitude,
        },
      };
    }

    // Only include remoteLocation if it's a remote event and remoteLocation is provided
    if (values.locationType === 'remote' && values.remoteLocation) {
      payload.remoteLocation = values.remoteLocation;
    }

    // Only include message if provided
    if (values.message) {
      payload.message = values.message;
    }

    campfireMutation
      .run({
        url: `${apiUrl}/vm/task/event/schedule`,
        method: 'post',
        data: payload,
      })
      .then((res: any) => {
        if (res.ok) {
          const isRescheduling = selectedEvent !== null;
          setSnackbar({
            open: true,
            message: `Event ${isRescheduling ? 'rescheduled' : 'scheduled'} successfully`,
            variant: 'success',
          });
          setShowAddDialog(false);
          setSelectedEvent(null); // Clear selected event after operation
          setSelectedManagers([]); // Clear selected managers after operation
          if (onRefresh) onRefresh();
        } else {
          const isRescheduling = selectedEvent !== null;
          console.error('❌ API returned error:', res);
          setSnackbar({
            open: true,
            message: `Failed to ${isRescheduling ? 'reschedule' : 'schedule'} event`,
            variant: 'error',
          });
        }
      })
      .catch((error) => {
        const isRescheduling = selectedEvent !== null;
        console.error('💥 API request failed:', error);
        setSnackbar({
          open: true,
          message: `Failed to ${isRescheduling ? 'reschedule' : 'schedule'} event`,
          variant: 'error',
        });
      });
  };

  const handleCompleteEvent = (values: any) => {
    if (!selectedEvent) return;

    // Format task field values properly with __typename
    const taskFieldValues: any[] = [];
    Object.keys(values).forEach((fieldId) => {
      const value = values[fieldId];
      if (value !== undefined && value !== null && value !== '') {
        // Find the field definition to determine the type
        const field = taskItems?.find((item: any) => item.taskFieldId === fieldId);
        if (field) {
          // For now, assume it's a short text field - this could be expanded based on field type
          taskFieldValues.push({
            __typename: 'VOLUNTEER_ShortTextTaskFieldValue',
            taskFieldId: fieldId,
            shortTextValue: value,
          });
        }
      }
    });

    const payload = {
      userId: user.userIdentityService?.userId || '',
      taskId,
      taskFieldValues,
      eventId: selectedEvent.completedTaskEventId,
    };

    campfireMutation
      .run({
        url: `${apiUrl}/vm/volunteer/profile/completed-task/save`,
        method: 'post',
        data: payload,
      })
      .then((res: any) => {
        if (res.ok) {
          setSnackbar({
            open: true,
            message: 'Event marked as complete successfully',
            variant: 'success',
          });
          setShowCompleteDialog(false);
          setSelectedEvent(null);
          if (onRefresh) onRefresh();
        } else {
          setSnackbar({
            open: true,
            message: 'Failed to complete event',
            variant: 'error',
          });
        }
      })
      .catch(() => {
        setSnackbar({
          open: true,
          message: 'Failed to complete event',
          variant: 'error',
        });
      });
  };

  const handleCancelEvent = () => {
    if (!selectedEvent) return;

    campfireMutation
      .run({
        url: `${apiUrl}/vm/task/event/cancel`,
        method: 'post',
        data: {
          eventId: selectedEvent.completedTaskEventId,
        },
      })
      .then((res: any) => {
        if (res.ok) {
          setSnackbar({
            open: true,
            message: 'Event cancelled successfully',
            variant: 'success',
          });
          setShowCancelDialog(false);
          setSelectedEvent(null);
          if (onRefresh) onRefresh();
        } else {
          setSnackbar({
            open: true,
            message: 'Failed to cancel event',
            variant: 'error',
          });
        }
      })
      .catch(() => {
        setSnackbar({
          open: true,
          message: 'Failed to cancel event',
          variant: 'error',
        });
      });
  };

  const parseDateTime = (dateTime: string) => {
    // Strip timezone info and parse as local time to prevent unwanted conversion
    const dateTimeWithoutTz = dateTime.replace(/[+-]\d{2}:?\d{0,2}$|Z$/g, '');
    // Use fromISO with the stripped datetime - this will treat it as local time
    return DateTime.fromISO(dateTimeWithoutTz);
  };

  const getStatusStyle = (status: EventData['status']) => {
    switch (status) {
      case 'completed':
        return {
          backgroundColor: '#e8f5e8',
          color: '#2e7d32',
        };
      case 'scheduled':
        return {
          backgroundColor: '#e3f2fd',
          color: '#1976d2',
        };
      case 'cancelled':
        return {
          backgroundColor: '#ffebee',
          color: '#c62828',
        };
      case 'no_show':
        return {
          backgroundColor: '#fff3e0',
          color: '#ef6c00',
        };
      default:
        return {
          backgroundColor: '#f5f5f5',
          color: '#666',
        };
    }
  };

  const getStatusIcon = (status: EventData['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle style={{ fontSize: '14px', marginRight: '4px', color: '#2e7d32' }} />;
      case 'scheduled':
        return <HourglassEmpty style={{ fontSize: '14px', marginRight: '4px', color: '#1976d2' }} />;
      case 'cancelled':
        return <Cancel style={{ fontSize: '14px', marginRight: '4px', color: '#c62828' }} />;
      case 'no_show':
        return <Cancel style={{ fontSize: '14px', marginRight: '4px', color: '#ef6c00' }} />;
      default:
        return null;
    }
  };

  const formatStatusLabel = (status: EventData['status']) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'scheduled':
        return 'Scheduled';
      case 'cancelled':
        return 'Cancelled';
      case 'no_show':
        return 'No Show';
      default:
        return status;
    }
  };

  const formatDateTime = (dateTime: string) => {
    if (!dateTime) return 'Invalid date';

    const date = parseDateTime(dateTime);

    if (!date.isValid) {
      return 'Invalid date';
    }

    // Format without timezone conversion
    return date.toLocaleString({
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDateTimeRange = (startDateTime: string, endDateTime?: string) => {
    const startDate = parseDateTime(startDateTime);

    if (!startDate.isValid) {
      return { time: 'Invalid time', date: 'Invalid date' };
    }

    const timeStr = startDate.toLocaleString({
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });

    const dateStr = startDate.toLocaleString({
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    if (!endDateTime) {
      return { time: timeStr, date: dateStr };
    }

    const endDate = parseDateTime(endDateTime);

    // If same day, just show time range
    if (startDate.isValid && endDate.isValid && startDate.hasSame(endDate, 'day')) {
      const endTimeStr = endDate.toLocaleString({
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
      return { time: `${timeStr} - ${endTimeStr}`, date: dateStr };
    }

    // Different days
    const endFormatted = formatDateTime(endDateTime);
    return { time: timeStr, date: `${dateStr} - ${endFormatted}` };
  };

  const renderEventItem = (event: EventData, index: number) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const statusStyle = getStatusStyle(event.status);

    // Check if event date is today or in the past
    const isEventTodayOrPast = useMemo(() => {
      if (!event.startDateTime) return false;
      const eventDate = parseDateTime(event.startDateTime);
      const today = DateTime.local().startOf('day');
      return eventDate.startOf('day') <= today;
    }, [event.startDateTime]);

    return (
      <Box>
        {index > 0 && <Divider style={{ margin: '20px 0' }} />}

        {/* Event Header with Status */}
        <Box display='flex' alignItems='center' justifyContent='space-between' mb={2}>
          <Typography variant='h6' style={{ fontWeight: 'bold', fontSize: '20px' }}>
            Event {events.length > 1 ? `${index + 1}` : ''}
          </Typography>
          <Box display='flex' alignItems='center' gap={1}>
            {event.status === 'scheduled' && !isReadOnly ? (
              isEventTodayOrPast ? (
                <ButtonGroup variant='outlined' size='small'>
                  <Button
                    onClick={() => {
                      setSelectedEvent(event);
                      setShowCompleteDialog(true);
                    }}
                    style={{
                      borderRadius: '20px 0 0 20px',
                      padding: '6px 16px',
                      fontSize: '12px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      lineHeight: '14px',
                      marginTop: '1px',
                      borderColor: '#e0e0e0',
                      color: 'rgb(84, 84, 84)',
                      backgroundColor: '#fafafa',
                      boxShadow: 'none',
                    }}
                    startIcon={<CheckCircle style={{ fontSize: '16px', color: '#2e7d32' }} />}
                    endIcon={<ChevronRight style={{ fontSize: '16px' }} />}
                  >
                    Complete
                  </Button>
                  <Button
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    style={{
                      borderRadius: '0 20px 20px 0',
                      padding: '6px 8px',
                      fontSize: '12px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      lineHeight: '14px',
                      marginTop: '1px',
                      borderColor: '#e0e0e0',
                      borderLeft: '1px solid #e0e0e0',
                      color: 'rgb(84, 84, 84)',
                      backgroundColor: '#fafafa',
                      boxShadow: 'none',
                      minWidth: '36px',
                    }}
                  >
                    <ArrowDropDown style={{ fontSize: '20px' }} />
                  </Button>
                </ButtonGroup>
              ) : (
                <Button
                  variant='outlined'
                  size='small'
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  style={{
                    borderRadius: '20px',
                    padding: '6px 16px',
                    fontSize: '12px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    lineHeight: '14px',
                    marginTop: '1px',
                    borderColor: '#e0e0e0',
                    color: 'rgb(84, 84, 84)',
                    backgroundColor: '#fafafa',
                    boxShadow: 'none',
                  }}
                  startIcon={<HourglassEmpty style={{ fontSize: '16px', color: '#1976d2' }} />}
                  endIcon={<KeyboardArrowDown style={{ fontSize: '16px' }} />}
                >
                  Scheduled
                </Button>
              )
            ) : event.status === 'completed' && !isReadOnly ? (
              <Button
                variant='outlined'
                size='small'
                onClick={() => {
                  setSelectedEvent(event);
                  setShowViewDialog(true);
                }}
                style={{
                  borderRadius: '20px',
                  padding: '6px 16px',
                  fontSize: '12px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  lineHeight: '14px',
                  marginTop: '1px',
                  borderColor: '#e0e0e0',
                  color: 'rgb(84, 84, 84)',
                  backgroundColor: '#fafafa',
                  boxShadow: 'none',
                }}
                startIcon={<Visibility style={{ fontSize: '16px', color: '#1976d2' }} />}
              >
                View
              </Button>
            ) : event.status === 'cancelled' && !isReadOnly ? (
              <Button
                variant='outlined'
                size='small'
                onClick={(e) => setAnchorEl(e.currentTarget)}
                style={{
                  borderRadius: '20px',
                  padding: '6px 16px',
                  fontSize: '12px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  lineHeight: '14px',
                  marginTop: '1px',
                  borderColor: '#e0e0e0',
                  color: 'rgb(84, 84, 84)',
                  backgroundColor: '#fafafa',
                  boxShadow: 'none',
                }}
                startIcon={getStatusIcon(event.status)}
                endIcon={<KeyboardArrowDown style={{ fontSize: '16px' }} />}
              >
                {formatStatusLabel(event.status)}
              </Button>
            ) : (
              <Box
                style={{
                  borderRadius: '20px',
                  padding: '6px 16px',
                  fontSize: '12px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  lineHeight: '14px',
                  marginTop: '1px',
                  backgroundColor: statusStyle.backgroundColor,
                  color: statusStyle.color,
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                {getStatusIcon(event.status)}
                {formatStatusLabel(event.status)}
              </Box>
            )}
          </Box>
        </Box>

        {/* Action Menu Dropdown */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            style: {
              borderRadius: '8px',
              marginTop: '8px',
              minWidth: '180px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            },
          }}
        >
          {event.status === 'cancelled' || event.status === 'completed' ? (
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                setSelectedEvent(event);
                // Pre-populate managers from existing event
                if (event.managers) {
                  const eventManagers = event.managers.map((manager) => ({
                    volunteerId: manager.managerId,
                    profile: {
                      profileId: manager.profile.profileId,
                      firstName: manager.profile.firstName,
                      lastName: manager.profile.lastName,
                      preferredName: manager.profile.preferredName,
                      avatarUrl: manager.profile.avatarUrl,
                    },
                  }));
                  setSelectedManagers(eventManagers);
                }
                setShowAddDialog(true);
              }}
              style={{
                fontSize: '14px',
                padding: '10px 16px',
              }}
            >
              <Replay style={{ fontSize: '18px', marginRight: '12px', color: '#ff9800' }} />
              Reschedule
            </MenuItem>
          ) : (
            <>
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  setSelectedEvent(event);
                  setShowCompleteDialog(true);
                }}
                style={{
                  fontSize: '14px',
                  padding: '10px 16px',
                }}
              >
                <CheckCircle style={{ fontSize: '18px', marginRight: '12px', color: '#4caf50' }} />
                Complete
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  setSelectedEvent(event);
                  // Pre-populate managers from existing event
                  if (event.managers) {
                    const eventManagers = event.managers.map((manager) => ({
                      volunteerId: manager.managerId,
                      profile: {
                        profileId: manager.profile.profileId,
                        firstName: manager.profile.firstName,
                        lastName: manager.profile.lastName,
                        preferredName: manager.profile.preferredName,
                        avatarUrl: manager.profile.avatarUrl,
                      },
                    }));
                    setSelectedManagers(eventManagers);
                  }
                  setShowAddDialog(true);
                }}
                style={{
                  fontSize: '14px',
                  padding: '10px 16px',
                }}
              >
                <Replay style={{ fontSize: '18px', marginRight: '12px', color: '#ff9800' }} />
                Reschedule
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  setSelectedEvent(event);
                  setShowCancelDialog(true);
                }}
                style={{
                  fontSize: '14px',
                  padding: '10px 16px',
                }}
              >
                <Cancel style={{ fontSize: '18px', marginRight: '12px', color: '#f44336' }} />
                Cancel Event
              </MenuItem>
            </>
          )}
        </Menu>

        {/* Cancelled Event Alert */}
        {event.status === 'cancelled' && (
          <Box mb={2}>
            <AlertCard variant='urgent' title='Event Cancelled'>
              <AlertCardBody>
                <Typography
                  variant='body2'
                  style={{ fontSize: '14px', color: getAlertCardColors('urgent').lightTextColor }}
                >
                  This event has been cancelled and is no longer scheduled.
                  {event.originalScheduledDate && (
                    <>
                      {' '}
                      Originally scheduled for <strong>{formatDateTime(event.originalScheduledDate)}</strong>.
                    </>
                  )}
                </Typography>
              </AlertCardBody>
            </AlertCard>
          </Box>
        )}

        {/* Date and Time */}
        <Box display='flex' alignItems='center' mb={2}>
          <Schedule style={{ fontSize: '20px', marginRight: '12px', color: '#1976d2' }} />
          <Box>
            <Typography variant='body2' style={{ fontWeight: 600, fontSize: '14px' }}>
              {(() => {
                const { time, date } = formatDateTimeRange(event.startDateTime || '', event.endDateTime);
                return `${date}, ${time}`;
              })()}
            </Typography>
            {event.endDateTime && (
              <Typography variant='caption' style={{ fontSize: '12px', color: '#808080' }}>
                Duration:{' '}
                {(() => {
                  const start = parseDateTime(event.startDateTime || '');
                  const end = parseDateTime(event.endDateTime);
                  if (!start.isValid || !end.isValid) return 'Unknown';
                  const durationMs = end.toMillis() - start.toMillis();
                  if (durationMs < 0) return 'Invalid';
                  const hours = Math.floor(durationMs / (1000 * 60 * 60));
                  const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
                  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
                })()}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Location */}
        <Box display='flex' alignItems='flex-start' mb={2}>
          {(() => {
            if (event.placesAddress) {
              return (
                <>
                  <LocationOn style={{ fontSize: '20px', marginRight: '12px', color: '#757575' }} />
                  <Box flex={1}>
                    <Typography variant='body2' style={{ fontWeight: 600, fontSize: '14px' }}>
                      {event.placesAddress.description}
                    </Typography>
                    <Typography variant='body2' style={{ fontSize: '14px', color: '#808080' }}>
                      {event.placesAddress.formatted}
                    </Typography>
                  </Box>
                </>
              );
            }

            if (event.remoteLocation) {
              const location = event.remoteLocation.trim();
              const isUrl = location.match(/^https?:\/\//) || location.includes('.com') || location.includes('.org');
              const isPhone = location.match(/^[+]?[\d\s\-()]+$/);

              // Platform detection for meeting links
              const getPlatformInfo = () => {
                if (location.includes('zoom.us')) {
                  return { label: 'Join Zoom Meeting', color: '#2d8cff' };
                }
                if (location.includes('meet.google.com')) {
                  return { label: 'Join Google Meet', color: '#34a853' };
                }
                if (location.includes('teams.microsoft.com')) {
                  return { label: 'Join Microsoft Teams', color: '#6264a7' };
                }
                if (isUrl) {
                  return { label: 'Join Video Call', color: '#1976d2' };
                }
                if (isPhone) {
                  return { label: `Call ${location}`, color: '#4caf50' };
                }
                return { label: 'Remote', color: '#757575' };
              };

              const platformInfo = getPlatformInfo();
              const fullUrl = isUrl && !location.startsWith('http') ? `https://${location}` : location;

              return (
                <>
                  {isUrl ? (
                    <VideoCall style={{ fontSize: '20px', marginRight: '12px', color: '#757575' }} />
                  ) : isPhone ? (
                    <Phone style={{ fontSize: '20px', marginRight: '12px', color: '#757575' }} />
                  ) : (
                    <VideoCall style={{ fontSize: '20px', marginRight: '12px', color: '#757575' }} />
                  )}
                  <Box flex={1}>
                    <Typography variant='body2' style={{ fontWeight: 600, fontSize: '14px' }}>
                      {isUrl ? 'Video Call' : isPhone ? 'Phone Call' : 'Remote'}
                    </Typography>
                    <Typography variant='body2' style={{ fontSize: '14px', color: '#808080', marginBottom: '8px' }}>
                      {event.remoteLocation}
                    </Typography>
                    {/* Join/Call Button */}
                    {(isUrl || isPhone) && event.status === 'scheduled' && (
                      <Button
                        variant='contained'
                        size='small'
                        onClick={() => {
                          if (isPhone) {
                            window.open(`tel:${location}`, '_self');
                          } else {
                            window.open(fullUrl, '_blank');
                          }
                        }}
                        style={{
                          backgroundColor: platformInfo.color,
                          color: 'white',
                          fontSize: '12px',
                          padding: '6px 12px',
                          textTransform: 'none',
                        }}
                        startIcon={
                          isPhone ? <Phone style={{ fontSize: '14px' }} /> : <VideoCall style={{ fontSize: '14px' }} />
                        }
                        endIcon={isUrl ? <Launch style={{ fontSize: '12px' }} /> : undefined}
                      >
                        {platformInfo.label}
                      </Button>
                    )}
                  </Box>
                </>
              );
            }

            return (
              <>
                <LocationOn style={{ fontSize: '20px', marginRight: '12px', color: '#757575' }} />
                <Box flex={1}>
                  <Typography variant='body2' style={{ fontWeight: 600, fontSize: '14px' }}>
                    Location TBD
                  </Typography>
                </Box>
              </>
            );
          })()}
        </Box>

        {/* Managers with profile avatars */}
        {event.managers && event.managers.length > 0 && (
          <Box display='flex' alignItems='flex-start' mb={3}>
            <Person style={{ fontSize: '20px', marginRight: '12px', color: '#757575' }} />
            <Box flex={1}>
              <Typography variant='body2' style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
                Managers Attending
              </Typography>
              <Box display='flex' flexDirection='column' style={{ gap: '8px' }}>
                {event.managers.map((manager) => (
                  <Box
                    key={manager.managerId}
                    sx={{
                      '& .MuiTypography-body1': {
                        fontSize: '12px !important',
                        lineHeight: '12px !important',
                      },
                    }}
                  >
                    <VolunteerProfile
                      avatarUrl={manager.profile?.avatarUrl || null}
                      name={`${manager.profile?.preferredName || manager.profile?.firstName || ''} ${manager.profile
                        ?.lastName || ''}`}
                      profileId={manager.profile?.profileId || ''}
                      userId={manager.profile?.userId || undefined}
                      lastName={manager.profile?.lastName || ''}
                      preferredName={manager.profile?.preferredName || manager.profile?.firstName || ''}
                      noTrim
                      tooltipAll={false}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {/* Volunteer Message */}
        {event.volunteerMessage && (
          <Box mb={2} p={2} bgcolor='#f0f7ff' borderRadius={1} border='1px solid #d0e3f7'>
            <Box display='flex' alignItems='flex-start'>
              <Message style={{ fontSize: '18px', marginRight: '8px', color: '#1976d2' }} />
              <Box flex={1}>
                <Typography
                  variant='body2'
                  style={{ fontWeight: 600, fontSize: '14px', color: '#1976d2', marginBottom: '4px' }}
                >
                  Message to Applicant
                </Typography>
                {(() => {
                  try {
                    const contentState = convertFromRaw(JSON.parse(event.volunteerMessage));
                    const editorState = EditorState.createWithContent(contentState);
                    return <CampfireRichEditor readOnly editorState={editorState} />;
                  } catch {
                    // If parsing fails, display as plain text
                    return (
                      <Typography variant='body2' style={{ fontSize: '14px', color: '#424242' }}>
                        {event.volunteerMessage}
                      </Typography>
                    );
                  }
                })()}
              </Box>
            </Box>
          </Box>
        )}
        {/* Participant Feedback */}
        {event.participantFeedback && (
          <Box display='flex' alignItems='flex-start' mb={2}>
            <CheckCircle style={{ fontSize: '20px', marginRight: '12px', color: '#4caf50' }} />
            <Box flex={1}>
              <Typography variant='body2' style={{ fontWeight: 600, fontSize: '14px' }}>
                Volunteer Feedback
              </Typography>
              <Typography variant='body2' style={{ fontSize: '14px', color: '#808080' }}>
                {event.participantFeedback}
              </Typography>
            </Box>
          </Box>
        )}
        {/* Reschedule count */}
        {event.rescheduleCount > 0 && (
          <Box mt={2}>
            <Typography variant='caption' style={{ fontSize: '12px', color: '#9e9e9e', fontStyle: 'italic' }}>
              This event has been rescheduled {event.rescheduleCount} time{event.rescheduleCount > 1 ? 's' : ''}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box
      marginTop={2}
      marginBottom={3}
      paddingTop={3}
      paddingBottom={3}
      paddingLeft={4}
      paddingRight={4}
      border={'1px solid #e9e9e9'}
      bgcolor='#ffffff'
      borderRadius={7}
    >
      {events.length === 0 ? (
        <Typography variant='body2' color='textSecondary'>
          No events scheduled for this task.
        </Typography>
      ) : (
        events.map((event, index) => renderEventItem(event, index))
      )}

      {!isReadOnly && events.length === 0 && (
        <Box mb={2} mt={3}>
          <TabletButton
            variant='outlined'
            color='primary'
            onClick={() => {
              setSelectedEvent(null);
              setShowAddDialog(true);
            }}
            size='small'
            fullWidth
            disabled={campfireMutation.isLoading}
          >
            Schedule
          </TabletButton>
        </Box>
      )}

      {/* Schedule Event Dialog */}
      <Dialog
        open={showAddDialog}
        onClose={() => {
          setShowAddDialog(false);
          setSelectedEvent(null);
          setSelectedManagers([]);
        }}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {selectedEvent ? 'Reschedule Event' : 'Schedule Event'}
          <IconButton
            aria-label='close'
            onClick={() => {
              setShowAddDialog(false);
              setSelectedEvent(null);
              setSelectedManagers([]);
            }}
            style={{ color: '#666' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <Formik
          initialValues={(() => {
            // If rescheduling an existing event, populate with current data
            if (selectedEvent) {
              const defaultTimes = getDefaultTimes();

              // Check both placesAddress and remoteLocation to determine location type
              const hasPhysicalLocation =
                selectedEvent.placesAddress &&
                (selectedEvent.placesAddress.placesId ||
                  selectedEvent.placesAddress.description ||
                  selectedEvent.placesAddress.formatted);

              const hasRemoteLocation = selectedEvent.remoteLocation && selectedEvent.remoteLocation.trim() !== '';

              // Determine location type - prioritize physical location if both exist
              let locationType = 'local'; // default
              if (hasPhysicalLocation) {
                locationType = 'local';
              } else if (hasRemoteLocation) {
                locationType = 'remote';
              }

              const initialValues = {
                startDateTime: selectedEvent.startDateTime
                  ? parseDateTime(selectedEvent.startDateTime).toFormat("yyyy-MM-dd'T'HH:mm")
                  : defaultTimes.startDateTime,
                endDateTime: selectedEvent.endDateTime
                  ? parseDateTime(selectedEvent.endDateTime).toFormat("yyyy-MM-dd'T'HH:mm")
                  : '',
                timezone: selectedEvent.timezone || getDefaultTimezone(timezoneOptions),
                locationType: locationType,
                location: hasPhysicalLocation
                  ? {
                      placesId: selectedEvent.placesAddress.placesId || '',
                      description: selectedEvent.placesAddress.description || '',
                      formatted: selectedEvent.placesAddress.formatted || '',
                      latitude: selectedEvent.placesAddress.latLong?.lat || null,
                      longitude: selectedEvent.placesAddress.latLong?.long || null,
                    }
                  : null,
                remoteLocation: selectedEvent.remoteLocation || '',
                message: selectedEvent.volunteerMessage || null,
              };

              return initialValues;
            }

            // Default values for new events
            const defaultTimes = getDefaultTimes();
            return {
              startDateTime: defaultTimes.startDateTime,
              endDateTime: '',
              timezone: getDefaultTimezone(timezoneOptions),
              locationType: 'local',
              location: null,
              remoteLocation: '',
              message: null,
            };
          })()}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting }) => {
            handleScheduleEvent(values);
            setSubmitting(false);
          }}
        >
          {({ values, handleChange, setFieldValue, handleBlur, errors, touched, isValid, initialValues }) => {
            // Check if form values have changed from initial values
            const hasFormChanged = useMemo(() => {
              // Deep comparison of form values (excluding managers since they're separate state)
              const currentFormValues = {
                startDateTime: values.startDateTime,
                endDateTime: values.endDateTime,
                timezone: values.timezone,
                locationType: values.locationType,
                location: values.location?.placesId || null, // Compare by placesId for location objects
                remoteLocation: values.remoteLocation,
                message: values.message,
              };

              const initialFormValues = {
                startDateTime: initialValues.startDateTime,
                endDateTime: initialValues.endDateTime,
                timezone: initialValues.timezone,
                locationType: initialValues.locationType,
                location: initialValues.location?.placesId || null,
                remoteLocation: initialValues.remoteLocation,
                message: initialValues.message,
              };

              // Also check if managers have changed
              const initialManagers = selectedEvent?.managers?.map((m) => m.managerId).sort() || [];
              const currentManagers = selectedManagers.map((m) => m.volunteerId).sort();
              const managersChanged = JSON.stringify(initialManagers) !== JSON.stringify(currentManagers);

              return JSON.stringify(currentFormValues) !== JSON.stringify(initialFormValues) || managersChanged;
            }, [values, initialValues, selectedEvent, selectedManagers]);

            return (
              <Form>
                <DialogContent>
                  <Grid container spacing={3}>
                    {/* Start Time - Required */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type='datetime-local'
                        label='Start Date & Time'
                        name='startDateTime'
                        value={values.startDateTime || ''}
                        onChange={(e) => {
                          const newStartTime = e.target.value;
                          setFieldValue('startDateTime', newStartTime);

                          // Clear end time if it's now before the new start time
                          if (values.endDateTime && newStartTime) {
                            const startDate = DateTime.fromISO(newStartTime);
                            const endDate = DateTime.fromISO(values.endDateTime);

                            if (startDate.isValid && endDate.isValid && endDate <= startDate) {
                              setFieldValue('endDateTime', '');
                            }
                          }
                        }}
                        onBlur={handleBlur}
                        error={!!(errors.startDateTime && touched.startDateTime)}
                        helperText={errors.startDateTime && touched.startDateTime ? errors.startDateTime : ''}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        InputProps={{
                          inputProps: {
                            min: getCurrentDateTimeString(),
                          },
                        }}
                        variant='outlined'
                        required
                        style={{ marginTop: '8px' }}
                      />
                    </Grid>

                    {/* End Time - Optional */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type='datetime-local'
                        label='End Time (Optional)'
                        name='endDateTime'
                        value={values.endDateTime || ''}
                        onChange={(e) => setFieldValue('endDateTime', e.target.value)}
                        onBlur={handleBlur}
                        error={!!(errors.endDateTime && touched.endDateTime)}
                        helperText={errors.endDateTime && touched.endDateTime ? errors.endDateTime : ''}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        InputProps={{
                          inputProps: {
                            min: values.startDateTime || undefined,
                          },
                        }}
                        variant='outlined'
                        style={{ marginTop: '8px' }}
                        disabled={!values.startDateTime}
                      />
                      {values.endDateTime && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                          <Button
                            size='small'
                            color='secondary'
                            onClick={() => setFieldValue('endDateTime', '')}
                            style={{
                              fontSize: '10px',
                              minWidth: 'auto',
                              padding: '2px 8px',
                              color: '#f44336',
                            }}
                          >
                            Reset
                          </Button>
                        </div>
                      )}
                    </Grid>

                    {/* Timezone */}
                    <Grid item xs={12}>
                      <FormControl fullWidth variant='outlined' required>
                        <InputLabel>Timezone</InputLabel>
                        <Select name='timezone' value={values.timezone} onChange={handleChange} label='Timezone'>
                          {timezoneOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Location Type */}
                    <Grid item xs={12}>
                      <Typography variant='body2' style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>
                        Location Type
                      </Typography>
                      <Box display='flex' width='100%'>
                        <Button
                          fullWidth
                          className={`${classes.button} ${
                            values.locationType === 'local' ? classes.toggleButtonSelected : classes.toggleButton
                          }`}
                          onClick={() => {
                            handleChange({ target: { name: 'locationType', value: 'local' } });
                          }}
                          style={{ marginRight: '8px' }}
                        >
                          <Typography style={{ fontWeight: 600, fontSize: '13px' }}>Specific Location</Typography>
                        </Button>
                        <Button
                          fullWidth
                          className={`${classes.button} ${
                            values.locationType === 'remote' ? classes.toggleButtonSelected : classes.toggleButton
                          }`}
                          onClick={() => {
                            handleChange({ target: { name: 'locationType', value: 'remote' } });
                          }}
                        >
                          <Typography style={{ fontWeight: 600, fontSize: '13px' }}>Remote/Virtual</Typography>
                        </Button>
                      </Box>
                    </Grid>

                    {/* Location Fields - Conditional */}
                    {values.locationType === 'local' ? (
                      <Grid item xs={12}>
                        <AutoLocationField
                          name='location'
                          label='Location'
                          fullWidth
                          variant='outlined'
                          required
                          helper
                          key={`location-${selectedEvent?.completedTaskEventId || 'new'}`}
                        />
                      </Grid>
                    ) : (
                      <Grid item xs={12}>
                        <Field
                          name='remoteLocation'
                          label='Remote Location Details'
                          placeholder='Meeting link, phone number, or instructions'
                          fullWidth
                          variant='outlined'
                          multiline
                          rows={2}
                          required
                        />
                      </Grid>
                    )}

                    {/* Manager Assignment */}
                    <Grid item xs={12}>
                      <Box>
                        <Typography variant='body2' style={{ marginBottom: '8px', fontWeight: 600 }}>
                          Assigned Managers (Optional)
                        </Typography>
                        {selectedManagers.length > 0 ? (
                          <Box mb={2}>
                            {selectedManagers.map((manager, index) => (
                              <Chip
                                key={manager.volunteerId}
                                label={`${manager.profile?.preferredName ||
                                  manager.profile?.firstName ||
                                  'Unknown'} ${manager.profile?.lastName || ''}`}
                                onDelete={() => {
                                  setSelectedManagers((prev) =>
                                    prev.filter((m) => m.volunteerId !== manager.volunteerId)
                                  );
                                }}
                                style={{
                                  marginRight: index < selectedManagers.length - 1 ? '16px' : 0,
                                  marginBottom: '12px',
                                  cursor: 'default',
                                }}
                                clickable={false}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant='body2' color='textSecondary' style={{ marginBottom: '8px' }}>
                            No managers assigned
                          </Typography>
                        )}
                        <Button
                          className={`${classes.button} ${classes.toggleButton}`}
                          onClick={() => {
                            setShowManagersDialog(true);
                          }}
                          disabled={!programId}
                        >
                          <Typography style={{ fontWeight: 600, fontSize: '13px' }}>
                            {selectedManagers.length > 0 ? 'Change Managers' : 'Select Managers'}
                          </Typography>
                        </Button>
                        {!programId && (
                          <Typography
                            variant='caption'
                            color='textSecondary'
                            display='block'
                            style={{ marginTop: '4px' }}
                          >
                            Program ID required for manager assignment
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    {/* Message for Volunteer */}
                    <Grid item xs={12}>
                      <Typography variant='body2' style={{ marginBottom: '8px', fontWeight: 600 }}>
                        Message for Volunteer (Optional)
                      </Typography>
                      <Typography
                        variant='caption'
                        color='textSecondary'
                        style={{ marginBottom: '12px', display: 'block' }}
                      >
                        This message will be shown to the volunteer
                      </Typography>
                      <RichEditorFormik
                        valueName='message'
                        placeholder='Provide any special instructions for the volunteer...'
                      />
                    </Grid>
                  </Grid>

                  {/* What Happens Next Info */}
                  <Grid item xs={12} style={{ marginTop: '24px' }}>
                    <AlertCard variant='info' title='What happens next?'>
                      <Typography variant='body2' style={{ color: '#1565C0' }}>
                        When this event is scheduled, email notifications and calendar invites will be sent to the
                        volunteer and any assigned managers. All participants will receive calendar entries with event
                        details and location information.
                      </Typography>
                    </AlertCard>
                  </Grid>
                </DialogContent>
                <DialogActions style={{ padding: '16px 24px', gap: '12px' }}>
                  <TabletButton
                    onClick={() => {
                      setShowAddDialog(false);
                      setSelectedEvent(null);
                      setSelectedManagers([]);
                    }}
                    variant='outlined'
                    color='primary'
                  >
                    Cancel
                  </TabletButton>
                  <TabletButton
                    type='submit'
                    variant='contained'
                    color='primary'
                    disabled={
                      campfireMutation.isLoading ||
                      !isValid ||
                      (selectedEvent && selectedEvent.status !== 'cancelled' && !hasFormChanged)
                    }
                    endIcon={<Send />}
                  >
                    {campfireMutation.isLoading ? 'Scheduling...' : selectedEvent ? 'Reschedule' : 'Schedule'}
                  </TabletButton>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </Dialog>

      {/* Complete Event Dialog */}
      <Dialog open={showCompleteDialog} onClose={() => setShowCompleteDialog(false)} maxWidth='md' fullWidth>
        <DialogTitle>Complete Event</DialogTitle>
        <Formik
          initialValues={{
            ...initTaskFormFormikValues(Array.isArray(taskItems) ? taskItems : [], taskItemValues || {}),
          }}
          onSubmit={handleCompleteEvent}
        >
          {({ values }) => (
            <Form>
              <DialogContent>
                {/* Task Fields if they exist */}
                {Array.isArray(taskItems) && taskItems.length > 0 ? (
                  <Box>
                    <Typography variant='subtitle1' style={{ fontWeight: 600, marginBottom: '16px' }}>
                      Task Fields
                    </Typography>
                    <TaskFormFieldList taskItems={deserializeTaskItems(taskItems)} values={values} />
                  </Box>
                ) : (
                  <Typography variant='body1' color='textSecondary'>
                    Are you sure you want to mark this event as complete?
                  </Typography>
                )}
              </DialogContent>
              <DialogActions style={{ padding: '16px 24px', gap: '12px' }}>
                <TabletButton onClick={() => setShowCompleteDialog(false)} variant='outlined' color='primary'>
                  Cancel
                </TabletButton>
                <TabletButton type='submit' variant='contained' color='primary' disabled={campfireMutation.isLoading}>
                  {campfireMutation.isLoading ? 'Completing...' : 'Complete Event'}
                </TabletButton>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* Cancel Event Confirmation */}
      <ConfirmationDialog
        open={showCancelDialog}
        title='Cancel Event'
        body='Are you sure you want to cancel this event? This action cannot be undone.'
        handleConfirmClick={handleCancelEvent}
        handleCloseClick={() => setShowCancelDialog(false)}
        confirmActionText='Cancel Event'
        closeActionText='Keep Event'
        disableConfirm={campfireMutation.isLoading}
      />

      {/* Manager Selection Dialog */}
      <MultiSelectVolunteersDialog
        open={showManagersDialog}
        title='Select Managers'
        volunteers={managersAsVolunteers}
        isLoading={managersQuery.loading}
        showSearchField
        ignoreVolunteerIds={selectedManagers.map((m) => m.volunteerId)}
        onClose={() => {
          setShowManagersDialog(false);
        }}
        onSubmit={(managers) => {
          setSelectedManagers(managers);
          setShowManagersDialog(false);
        }}
      >
        <Typography variant='body2' color='textSecondary'>
          Select managers to assign to this event. You can select multiple managers.
          {!programId && ' (Program ID required)'}
        </Typography>
      </MultiSelectVolunteersDialog>

      {/* View Completed Event Dialog */}
      <Dialog open={showViewDialog} onClose={() => setShowViewDialog(false)} maxWidth='md' fullWidth>
        <DialogTitle>View Completed Event</DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box>
              <Typography variant='h6' style={{ marginBottom: '16px' }}>
                Event Details
              </Typography>

              <Box mb={2}>
                <Typography variant='body2' color='textSecondary'>
                  Status: <strong>{selectedEvent.status}</strong>
                </Typography>
                {selectedEvent.startDateTime && (
                  <Typography variant='body2' color='textSecondary'>
                    Completed: <strong>{formatDateTime(selectedEvent.startDateTime)}</strong>
                  </Typography>
                )}
              </Box>

              {/* Show submitted task field data if available */}
              {Array.isArray(taskItems) && taskItems.length > 0 && taskItemValues ? (
                <Box mt={3}>
                  <Typography variant='h6' style={{ marginBottom: '16px' }}>
                    Submitted Data
                  </Typography>
                  {taskItems.map((item: any) => {
                    const value = taskItemValues[item.taskFieldId];
                    if (value === undefined || value === null || value === '') return null;

                    return (
                      <Box key={item.taskFieldId} mb={2}>
                        <Typography variant='subtitle2' style={{ fontWeight: 600 }}>
                          {item.name}
                        </Typography>
                        <Typography variant='body2' style={{ marginLeft: '8px' }}>
                          {value}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant='body2' color='textSecondary'>
                  No task data was submitted with this event.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <TabletButton onClick={() => setShowViewDialog(false)} variant='contained' color='primary'>
            Close
          </TabletButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
